<p-toast></p-toast>
<shared-confirm-dialog [dialogStyle]="{ width: '34rem', maxWidth: '90vw' }" />

<div class="mb-6 flex flex-wrap items-center justify-between gap-4">
  <div>
    <h1 class="!mt-0 !mb-2">Customers</h1>
    <p class="!mt-0 text-muted-color">Manage customer records for quotes.</p>
  </div>
  <p-button label="New Customer" icon="pi pi-plus" (onClick)="openDrawer()" />
</div>

@if (companies$ | async; as companies) {
  <div class="rounded-xl border border-surface-switch-200 shadow-sm">
    <!-- Header Row -->
    <div
      class="hidden lg:grid lg:grid-cols-7 rounded-t-xl bg-surface-switch-100 items-center gap-4 px-4 py-3 text-sm font-bold text-surface-switch-600 uppercase"
    >
      <div>Name</div>
      <div>Email</div>
      <div>Phone</div>
      <div>Website</div>
      <div>Quotes</div>
      <div>Created</div>
      <div class="text-center">Actions</div>
    </div>

    @if (companies.length > 0) {
      <div class="flex flex-col">
        @for (company of companies; track company.id) {
          <div
            class="border-b border-surface-switch-200 last:border-b-0 bg-surface-switch-50 transition-colors duration-200 hover:bg-surface-switch-100"
          >
            <!-- Mobile Layout -->
            <div class="lg:hidden p-4 flex flex-col gap-2">
              <div class="flex justify-between items-start">
                <span class="font-semibold text-lg">{{ company.name }}</span>
                <div class="flex gap-1 shrink-0 ml-2">
                  <button
                    pButton
                    [text]="true"
                    size="small"
                    severity="secondary"
                    icon="pi pi-pencil"
                    (click)="openDrawer(company)"
                    pTooltip="Edit"
                    tooltipPosition="top"
                  ></button>
                  <button
                    pButton
                    [text]="true"
                    size="small"
                    severity="danger"
                    icon="pi pi-trash"
                    (click)="confirmDelete(company)"
                    pTooltip="Delete"
                    tooltipPosition="top"
                  ></button>
                </div>
              </div>
              @if (company.email) {
                <div class="text-sm text-muted-color">{{ company.email }}</div>
              }
              @if (company.phone) {
                <div class="text-sm text-muted-color">{{ company.phone }}</div>
              }
              @if (company.website) {
                <div class="text-sm text-muted-color">
                  {{ company.website }}
                </div>
              }
              <div class="text-sm text-muted-color">
                {{ company.linked_quote_count }} quote{{
                  company.linked_quote_count === 1 ? "" : "s"
                }}
                linked
              </div>
              <div class="text-sm text-muted-color">
                {{ company.created_at | date: "mediumDate" }}
              </div>
            </div>

            <!-- Desktop Layout -->
            <div
              class="hidden lg:grid lg:grid-cols-7 lg:items-center lg:gap-4 lg:px-4 lg:py-3"
            >
              <div class="font-medium truncate">{{ company.name }}</div>
              <div class="text-sm truncate">{{ company.email || "—" }}</div>
              <div class="text-sm">{{ company.phone || "—" }}</div>
              <div class="text-sm truncate">{{ company.website || "—" }}</div>
              <div class="text-sm">
                {{ company.linked_quote_count }} quote{{
                  company.linked_quote_count === 1 ? "" : "s"
                }}
                linked
              </div>
              <div class="text-sm text-muted-color">
                {{ company.created_at | date: "mediumDate" }}
              </div>
              <div class="flex justify-center gap-1">
                <button
                  pButton
                  [text]="true"
                  size="small"
                  severity="secondary"
                  icon="pi pi-pencil"
                  (click)="openDrawer(company)"
                  pTooltip="Edit"
                  tooltipPosition="top"
                ></button>
                <button
                  pButton
                  [text]="true"
                  size="small"
                  severity="danger"
                  icon="pi pi-trash"
                  (click)="confirmDelete(company)"
                  pTooltip="Delete"
                  tooltipPosition="top"
                ></button>
              </div>
            </div>
          </div>
        }
      </div>
    } @else {
      <div class="text-center text-muted-color py-8">
        No customers yet. Click "New Customer" to add one.
      </div>
    }
  </div>
}

<p-drawer
  [(visible)]="isDrawerVisible"
  [header]="companyInDrawer ? 'Edit Customer' : 'New Customer'"
  position="right"
  [styleClass]="'!w-full lg:!w-1/2 2xl:!w-1/3'"
>
  <form [formGroup]="companyFormGroup" class="flex flex-col gap-6">
    <div class="flex flex-col gap-1.5">
      <label
        for="company-name"
        class="text-sm font-semibold text-surface-switch-900"
        >Company Name <span class="text-red-500">*</span></label
      >
      <input
        id="company-name"
        pInputText
        formControlName="name"
        class="w-full"
      />
    </div>

    <div class="flex flex-col gap-1.5">
      <label
        for="company-email"
        class="text-sm font-semibold text-surface-switch-900"
        >Email</label
      >
      <input
        id="company-email"
        pInputText
        formControlName="email"
        class="w-full"
      />
    </div>

    <div class="flex flex-col gap-1.5">
      <label
        for="company-phone"
        class="text-sm font-semibold text-surface-switch-900"
        >Phone</label
      >
      <input
        id="company-phone"
        pInputText
        formControlName="phone"
        class="w-full"
      />
    </div>

    <div class="flex flex-col gap-1.5">
      <label
        for="company-website"
        class="text-sm font-semibold text-surface-switch-900"
        >Website</label
      >
      <input
        id="company-website"
        pInputText
        formControlName="website"
        class="w-full"
      />
    </div>

    <div class="flex flex-col gap-1.5">
      <label
        for="company-address"
        class="text-sm font-semibold text-surface-switch-900"
        >Address</label
      >
      <textarea
        id="company-address"
        pTextarea
        formControlName="address"
        class="w-full"
        rows="4"
      ></textarea>
    </div>

    <div class="flex justify-end gap-2 pt-4">
      <p-button
        label="Cancel"
        severity="secondary"
        (onClick)="closeDrawer()"
        [disabled]="isSaving"
      />
      <p-button
        [label]="companyInDrawer ? 'Update Customer' : 'Create Customer'"
        icon="pi pi-save"
        (onClick)="saveCompany()"
        [loading]="isSaving"
        [disabled]="companyFormGroup.invalid || isSaving"
      />
    </div>
  </form>
</p-drawer>
